import { FC, useEffect, useRef } from 'react';

import { useHttpClient } from '@bonusly/http-client';

import { useSeasonalTheme } from '../seasonal-theme-context';
import { applyNeverRegress, computeTargetBloom, todayLocalDate } from './bloom-engine';
import {
	ActivityCache,
	BLOOM_DEBUG_MODE,
	getActivityCache,
	getBloomProgress,
	getLastComputedDate,
	setActivityCache,
	setBloomProgress,
	setLastComputedDate,
} from './bloom-store';

/**
 * How long the activity_count response stays fresh before we refetch on app
 * load. Real-time bloom updates are explicitly out of scope — next-load
 * freshness is the design target. 30 minutes hits the sweet spot between
 * "feels responsive across a workday" and "doesn't hammer the endpoint".
 */
const ACTIVITY_TTL_MS = 30 * 60 * 1000;

/**
 * Bloom orchestration runs exactly once per app load. Module scope (not a
 * `useRef`) is required because React 18 StrictMode intentionally double-
 * mounts components in dev — a per-component ref would still let both mount
 * cycles fire the network request. Module scope guarantees a single fetch
 * even with StrictMode, and the page reload on theme toggle clears it.
 *
 * Pairs intentionally with the lack of a `cancelled` flag below: the fetch
 * updates module-level state (not React state), so it's safe — and correct —
 * to let the response apply even after StrictMode's simulated unmount.
 */
let hasInitialized = false;

interface ActivityCountResult {
	given_count: number;
	received_count: number;
}

interface ActivityCountResponse {
	success: boolean;
	result: ActivityCountResult;
}

/**
 * Initializes spring bloom progress on mount.
 *
 * Lifecycle:
 *   1. Read cached bloom + cached activity → compute floor for today →
 *      `applyNeverRegress` → push the result through `setBloomProgress` so
 *      the floor advances even when offline (trigger: floor_tick or
 *      cache_init for first-run users).
 *   2. If the activity cache is missing, stale, or was fetched against a
 *      different enableDate, fetch /profile/recognition/activity_count.
 *      Failures are swallowed — bloom keeps the floor-only value.
 *   3. On a fresh fetch, recompute target with the new totalCount and push
 *      again (trigger: activity_lift) if it raised bloom.
 *
 * Bloom regression is impossible by construction: every push goes through
 * `applyNeverRegress` and `setBloomProgress` rejects downward changes when a
 * trigger is provided.
 */
export const useSpringBloom = () => {
	const { isSeasonalThemeEnabled, enableDate } = useSeasonalTheme();
	const http = useHttpClient('v2');

	// Mount-only initialization. Inputs are captured in refs so we can use
	// `[]` deps without lying to the linter — per the codebase convention for
	// effects that should run once after auth resolves.
	const inputsRef = useRef({ http, isSeasonalThemeEnabled, enableDate });
	inputsRef.current = { http, isSeasonalThemeEnabled, enableDate };

	useEffect(() => {
		// Visual testing mode: leave bloom under the debug slider's control.
		if (BLOOM_DEBUG_MODE) return;

		const { isSeasonalThemeEnabled: enabled, enableDate: date } = inputsRef.current;
		if (!enabled || !date) return;

		if (hasInitialized) return;
		hasInitialized = true;

		const today = todayLocalDate();

		// --- Step 1: floor + cached-activity recompute -----------------------
		const cachedBloom = getBloomProgress();
		const cachedActivity = getActivityCache();
		const isFirstRun = getLastComputedDate() === null && cachedBloom === 0;

		const initialTotal = cachedActivity?.enableDate === date ? cachedActivity.totalCount : 0;
		const initialTarget = computeTargetBloom(initialTotal, date, today);
		const initialNext = applyNeverRegress(cachedBloom, initialTarget);

		if (initialNext > cachedBloom) {
			setBloomProgress(initialNext, {
				trigger: isFirstRun ? 'cache_init' : 'floor_tick',
				totalCount: initialTotal,
			});
		}
		setLastComputedDate(today);

		// --- Step 2: refresh activity if stale -------------------------------
		const isFresh = cachedActivity && cachedActivity.enableDate === date && Date.now() - cachedActivity.fetchedAt < ACTIVITY_TTL_MS;
		if (isFresh) return;

		// Fire-and-forget. No cancellation: we update module-level bloom state,
		// not React state, so a late response from a "stale" mount cycle is
		// still a perfectly valid update. Letting it through also fixes a
		// StrictMode dev-only foot-gun where a cancellation closure combined
		// with the `hasInitialized` early-return would silently drop every
		// activity_lift response in development.
		const refreshActivity = async () => {
			try {
				const res = await inputsRef.current.http.get<ActivityCountResponse>('profile/recognition/activity_count', {
					params: { start_date: date },
				});

				const result = res.data?.result;
				if (!result) return;

				const totalCount = (result.given_count ?? 0) + (result.received_count ?? 0);
				const next: ActivityCache = { totalCount, fetchedAt: Date.now(), enableDate: date };
				setActivityCache(next);

				const target = computeTargetBloom(totalCount, date, todayLocalDate());
				const after = applyNeverRegress(getBloomProgress(), target);
				if (after > getBloomProgress()) {
					setBloomProgress(after, { trigger: 'activity_lift', totalCount });
				}
			} catch {
				// Endpoint failed (network, 401, 422, 404) — bloom stays at the
				// floor-only value. We don't surface the error: the theme should
				// never block on this fetch, and the next app load will retry.
			}
		};

		refreshActivity();
		// Run once on mount — see ref pattern above.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

/**
 * Renderless component that runs `useSpringBloom` and bails out for users
 * without the theme enabled. Mount it inside the spring `EffectsComponent`
 * so bloom orchestration only runs when the theme is active.
 */
export const SpringBloomInitializer: FC = () => {
	useSpringBloom();
	return null;
};
